import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { NSOrderFillConstants } from '@ruby/configs/order-fill.constants';
import { ICustomer } from '@ruby/modules/customer-select/models/customer.interface';
import {
  IOrderFillDateRange,
  IOrderFillLocation,
  IOrderFillPattern,
  IOrderFillPatternKey,
  IOrderFillPatterns,
  IOrderFillRequest,
  IOrderFillViewDateRange,
  IOrderLocationRequest
} from '@ruby/modules/order-fill/model/order-fill.interface';
import { EFeatureFlag } from '@ruby/shared/enums/feature-flags.enums';
import { EOrderFillDateInterval, EOrderFillDateType, EOrderViewType } from '@ruby/shared/enums/order-fill.enums';
import { LaunchDarklyService } from '@ruby/shared/services/analytics/launch-darkly.service';
import { NSDateUtils } from '@ruby/shared/services/utils/ns-date.utils';
import { NSValidators } from '@ruby/shared/validators/ns-validators.validator';

@Component({
  selector: 'ng-order-history-header',
  templateUrl: './order-history-header.component.html',
  styleUrls: ['./order-history-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderHistoryHeaderComponent implements OnInit, OnChanges {
  @Input() loadingLocations!: boolean;
  @Input() loadingPatterns!: boolean;
  @Input() loadingDateRange!: boolean;
  @Input() locations!: Array<IOrderFillLocation>;
  @Input() patterns!: Array<IOrderFillPattern>;
  @Input() classCode!: string;
  @Input() selectedCustomer?: ICustomer;
  @Input() customDateRange!: IOrderFillViewDateRange;
  @Input() dateInterval!: { previous?: EOrderFillDateInterval; current?: EOrderFillDateInterval };
  @Input() dateRange?: IOrderFillDateRange;
  @Output() updateDateInterval = new EventEmitter<{ previous: EOrderFillDateInterval; current: EOrderFillDateInterval }>();
  @Output() selectedOrderFill = new EventEmitter<IOrderFillRequest>();
  @Output() selectedDateInterval = new EventEmitter<EOrderFillDateType>();
  @Output() selectPatterns = new EventEmitter<Array<IOrderFillLocation>>();
  form: UntypedFormGroup = new UntypedFormGroup({
    dateRange: new UntypedFormControl(),
    patterns: new UntypedFormControl(undefined, NSValidators.requiredArrayValidator()),
    locations: new UntypedFormControl(undefined, NSValidators.requiredArrayValidator())
  });
  intervals: Array<EOrderFillDateInterval> = NSOrderFillConstants.dateIntervals;
  dateDescription = '';
  selectedLocations: Array<IOrderFillLocation> = [];
  selectedPatterns: Array<IOrderFillPatternKey> = [];
  patternKeys: Array<IOrderFillPatterns> = [];
  doofFeature = false;

  constructor(private launchDarklyService: LaunchDarklyService) {
  }

  get selectLocationsValuesLabel(): string {
    return (this.form.controls.locations.value as Array<IOrderFillLocation>)?.map((i: IOrderFillLocation) => i.locationName)?.join();
  }

  get selectPatternsValuesLabel(): string {
    return (this.form.controls.patterns.value as Array<IOrderFillPatternKey>)?.map((i: IOrderFillPatternKey) => i.value)?.join();
  }

  ngOnInit(): void {
    this.launchDarklyService.getFlagValue(EFeatureFlag.ACCESSNS_RUBY_DAILY_ORDER_FILL, this.doofFeature).then((value: boolean) => {
      this.doofFeature = value;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedCustomer?.currentValue && changes.selectedCustomer.currentValue !== changes.selectedCustomer.previousValue) {
      this.updateInterval(NSOrderFillConstants.dateIntervals[1]);
    }

    if (changes.customDateRange && changes.customDateRange.currentValue) {
      this.updateInterval(changes.customDateRange.currentValue.dateInterval.current);
      if (changes.customDateRange.currentValue.dateInterval.current === EOrderFillDateInterval.CUSTOM) {
        this.intervals = [EOrderFillDateInterval.CUSTOM, ...NSOrderFillConstants.dateIntervals];
      } else {
        this.intervals = NSOrderFillConstants.dateIntervals;
      }
    }

    if (changes.patterns?.currentValue) {
      if (changes.patterns.currentValue.length === 0) {
        this.patternKeys = [];
        this.form.controls.patterns.setValue(undefined);
      } else if (changes.patterns.currentValue.length > 0) {
        this.updatePatterns();
      }
    }

    if (changes.locations?.currentValue) {
      if (changes.locations.currentValue.length === 0) {
        this.selectedLocations = [];
        this.form.controls.locations.setValue(undefined);
        this.patternKeys = [];
        this.form.controls.patterns.setValue(undefined);
        this.patterns = [];
      } else if (changes.locations.currentValue.length > 0) {
        this.updateLocation();
      }
    }

    if (changes.dateRange?.currentValue && changes.dateRange.currentValue.startDate && changes.dateRange.currentValue.endDate) {
      this.updateDateDescription(changes.dateRange.currentValue);

      const formPatterns: Array<IOrderFillPatternKey> = this.form.controls.patterns.value;
      if (formPatterns && formPatterns.length > 0) {
        this.emitSelectedOrderFill(changes.dateRange.currentValue);
      }
    }
  }

  /**
   * Update the interval value
   *
   * @summary Updates the interval value and emit the current date range
   * @param $value: EOrderFillDateInterval
   * @returns void
   */
  updateInterval($value: EOrderFillDateInterval): void {
    this.updateDateInterval.emit({
      previous: this.dateInterval.current!,
      current: $value
    });

    this.form.controls.dateRange.setValue($value);
    this.intervals = NSOrderFillConstants.dateIntervals;
    const { dateRange } = this.calculateRange();

    if (dateRange !== EOrderFillDateType.CUSTOM) {
      this.selectedDateInterval.emit(dateRange);
    } else {
      this.emitSelectedOrderFill(this.customDateRange.range);
    }
  }

  /**
   * Resets filters
   *
   * @summary Resets the filters to their default state
   * @returns void
   */
  resetFilters(): void {
    this.updateInterval(NSOrderFillConstants.dateIntervals[1]);
    this.updateLocation();
    this.selectPatterns.emit(this.form.get('locations')!.value);
  }

  /**
   * Locations Changed
   *
   * @summary Emits selected location
   * @returns void
   */
  locationsChanged(): void {
    if (this.form.controls.locations.value?.length > 0 && JSON.stringify(this.selectedLocations) !== JSON.stringify(this.form.controls.locations.value)) {
      this.selectedLocations = this.form.controls.locations.value;
      this.selectPatterns.emit(this.form.get('locations')!.value);
    }
  }

  /**
   * Patterns Changed
   *
   * @summary Emits selected date range, viewType and locations
   * @returns void
   */
  patternsChanged(): void {
    if (this.form.controls.patterns.value?.length > 0 && JSON.stringify(this.selectedPatterns) !== JSON.stringify(this.form.controls.patterns.value)) {
      this.selectedPatterns = this.form.controls.patterns.value;

      const { dateRange } = this.calculateRange();
      if (dateRange !== EOrderFillDateType.CUSTOM) {
        this.emitSelectedOrderFill(this.dateRange!);
      } else {
        this.emitSelectedOrderFill(this.customDateRange.range);
      }
    }
  }

  private emitSelectedOrderFill(range: IOrderFillDateRange): void {
    const { viewType } = this.calculateRange();
    const locations: Array<IOrderLocationRequest> = [];
    const formPatterns: Array<IOrderFillPatternKey> = this.form.controls.patterns.value;

    if (formPatterns && formPatterns.length > 0) {
      const patternsKeys: Array<string> = [...new Set(formPatterns.map((pKey: IOrderFillPatternKey) => pKey.key))];
      patternsKeys.forEach((key: string) => {
        const pattern: IOrderFillPattern | undefined = this.patterns.find((pat: IOrderFillPattern) => pat.classCode === key);
        if (pattern) {
          const patternsCopy = [...pattern.patterns];
          pattern.patterns.forEach((name: string) => {
            const find = formPatterns.find((pat: IOrderFillPatternKey) => pat.key === key && name === pat.value);
            if (!find) {
              const index: number = patternsCopy.findIndex((value: string) => value === name);
              patternsCopy.splice(index, 1);
            }
          });
          locations.push({
            classCode: pattern.classCode,
            stationCode: pattern.stationCode,
            servingRoad: this.locations.find((location: IOrderFillLocation) => location.classCode === pattern.classCode)!.servingRoad,
            patterns: patternsCopy
          });
        }
      });
    }

    this.updateDateDescription(range);
    this.selectedOrderFill.emit({
      startDate: range.startDate,
      endDate: range.endDate,
      viewType,
      locations
    });
  }

  private updateDateDescription(dateRange: IOrderFillDateRange) {
    this.dateDescription = `${ NSDateUtils.formatNSDate(dateRange.startDate, 'M/d/yy') } - ${ NSDateUtils.formatNSDate(dateRange.endDate, 'M/d/yy') }`;
  }

  private calculateRange(): { dateRange: EOrderFillDateType; viewType: EOrderViewType } {
    let viewType: EOrderViewType = EOrderViewType.WEEKLY;
    let dateRange: EOrderFillDateType = EOrderFillDateType.LAST_WEEK;

    switch (this.form.controls.dateRange.value) {
      case EOrderFillDateInterval.LAST_WEEK:
        dateRange = EOrderFillDateType.LAST_WEEK;
        viewType = this.doofFeature ? EOrderViewType.DAILY : EOrderViewType.WEEKLY;
        break;
      case EOrderFillDateInterval.LAST_WEEKS:
        dateRange = EOrderFillDateType.LAST_WEEKS;
        viewType = EOrderViewType.WEEKLY;
        break;
      case EOrderFillDateInterval.LAST_MONTH:
        dateRange = EOrderFillDateType.LAST_MONTH;
        viewType = EOrderViewType.WEEKLY;
        break;
      case EOrderFillDateInterval.YEAR_TO_DATE:
        dateRange = EOrderFillDateType.YEAR_TO_DATE;
        viewType = EOrderViewType.MONTHLY;
        break;
      case EOrderFillDateInterval.LAST_YEAR:
        dateRange = EOrderFillDateType.LAST_YEAR;
        viewType = EOrderViewType.MONTHLY;
        break;
      case EOrderFillDateInterval.CUSTOM:
        dateRange = EOrderFillDateType.CUSTOM;
        viewType = this.customDateRange.viewType;
        break;
    }

    return { dateRange, viewType };
  }

  private updateLocation(): void {
    if (this.locations && this.locations.length > 0) {
      this.form.controls.locations.setValue(this.locations);
      this.selectedLocations = this.form.controls.locations.value;
      this.selectPatterns.emit(this.form.controls.locations.value);
    }
  }

  private updatePatterns(): void {
    const allPatterns: Array<IOrderFillPatternKey> = [];
    const orderFillPatterns: Array<IOrderFillPatterns> = [];
    this.patterns.forEach((pattern: IOrderFillPattern) => {
      const formPatterns: Array<IOrderFillPatternKey> = [];
      pattern.patterns.forEach((value: string) => {
        const patternKey = { key: pattern.classCode, value };
        formPatterns.push(patternKey);
        allPatterns.push(patternKey);
      });
      orderFillPatterns.push({ key: pattern.locationName, values: formPatterns });
    });
    this.patternKeys = orderFillPatterns;
    this.form.controls.patterns.setValue(allPatterns);
    this.selectedPatterns = allPatterns;

    const { dateRange } = this.calculateRange();
    if (dateRange !== EOrderFillDateType.CUSTOM) {
      this.emitSelectedOrderFill(this.dateRange!);
    } else {
      this.emitSelectedOrderFill(this.customDateRange.range);
    }
  }
}
